import React from 'react';
import styles from './Features.module.css';
import { Container } from 'components/shared';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';

const Section = ({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLElement>) => (
  <section
    className={className ? `${styles.section} ${className}` : styles.section}
    {...props}
  >
    {children}
  </section>
);

const CardsContainer = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.cardsContainer}>{children}</div>
);

const Card = ({ children }: { children: React.ReactNode }) => (
  <article className={styles.card}>{children}</article>
);

const CardImageWrapper = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.cardImageWrapper}>{children}</div>
);

const CardImage = (props: React.ImgHTMLAttributes<HTMLImageElement>) => (
  <img alt="" className={styles.cardImage} {...props} />
);

const CardVideo = (props: React.VideoHTMLAttributes<HTMLVideoElement>) => (
  <video className={styles.cardVideo} {...props} />
);

const TextContainer = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.textContainer}>{children}</div>
);

const CardTitle = ({ children }: { children: React.ReactNode }) => (
  <h3 className={styles.cardTitle}>{children}</h3>
);

const CardSubtitle = ({ children }: { children: React.ReactNode }) => (
  <p className={styles.cardSubtitle}>{children}</p>
);

// Feature data type - supports both image and video
type FeatureMedia =
  | { type: 'image'; src: string; alt: string }
  | { type: 'video'; src: string; poster?: string; alt?: string };

type FeatureData = {
  id: string;
  title: string;
  subtitle: string;
  media: FeatureMedia;
};

// Placeholder data - replace with actual image/video paths and content
const featuresData: FeatureData[] = [
  {
    id: 'feature-1',
    title: 'Sleek and Powerful',
    subtitle:
      'Ultrahuman Home is crafted from anodised aluminium — durable, minimal and elegant. No screens. No noise. An engineering marvel that blends into your space and works quietly in the background.',
    media: {
      type: 'image',
      src: '/web_v2/uh-home-v2/placeholders/features/00.png',
      alt: 'Front view of Ultrahuman Home device',
    },
  },
  {
    id: 'feature-2',
    title: 'Precision Sensors',
    subtitle:
      'Packed with precision microphones and sensors, Home understands and creates an idea environment to aid a restful sleep, each night.',
    media: {
      type: 'image',
      src: '/web_v2/uh-home-v2/placeholders/features/001-new.png',
      alt: 'Top view of Ultrahuman Home device',
    },
  },
  {
    id: 'feature-3',
    title: 'Privacy, built inside',
    subtitle:
      "With a physical microphone cut-off toggle, Home gives you complete control. It's designed to listen to your environment, not you.",
    media: {
      type: 'image',
      src: '/web_v2/uh-home-v2/placeholders/features/02.png',
      alt: 'Back view of Ultrahuman Home device',
    },
  },
];

type FeaturesProps = {
  className?: string;
  htmlId?: string;
};

const Features: React.FC<FeaturesProps> = ({ className, htmlId }) => {
  return (
    <Section
      className={className}
      id={htmlId}
      role="region"
      aria-labelledby={htmlId ? `${htmlId}-heading` : 'features-heading'}
    >
      <Container>
        <CardsContainer>
          {featuresData.map((feature) => (
            <Card key={feature.id}>
              <CardImageWrapper>
                {feature.media.type === 'image' ? (
                  <CardImage
                    src={getCompressedAssetUrl(feature.media.src)}
                    alt={feature.media.alt}
                  />
                ) : (
                  <CardVideo
                    src={getCompressedAssetUrl(feature.media.src)}
                    poster={
                      feature.media.poster
                        ? getCompressedAssetUrl(feature.media.poster)
                        : undefined
                    }
                    autoPlay
                    loop
                    muted
                    playsInline
                    aria-label={feature.media.alt || feature.title}
                  />
                )}
              </CardImageWrapper>
              <TextContainer>
                <CardTitle>{feature.title}</CardTitle>
                <CardSubtitle>{feature.subtitle}</CardSubtitle>
              </TextContainer>
            </Card>
          ))}
        </CardsContainer>
      </Container>
    </Section>
  );
};

export default Features;
